<template>
  <div 
    class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden max-w-sm mx-auto"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Image/Video Section -->
    <div class="relative">
      <!-- Video Element (hidden by default) -->
      <video
        ref="videoElement"
        :src="video.videoLink"
        class="w-full h-48 object-cover transition-opacity duration-300"
        :class="{ 'opacity-0': !isVideoPlaying, 'opacity-100': isVideoPlaying }"
        muted
        loop
        preload="metadata"
        @loadeddata="onVideoLoaded"
      ></video>
      
      <!-- Image Element -->
      <img
        :src="video.vignette || '/placeholder.svg?height=240&width=385'"
        :alt="video.name"
        class="w-full h-48 object-cover transition-opacity duration-300 absolute top-0 left-0"
        :class="{ 'opacity-100': !isVideoPlaying, 'opacity-0': isVideoPlaying }"
      />
      
      <!-- Duration Badge -->
      <div class="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white px-2 py-1 rounded text-xs font-bold z-10">
        {{ formatDuration(video.duration) }}
      </div>
      
      <!-- Play Icon Overlay (shows when not hovering) -->
      <div 
        class="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
        :class="{ 'opacity-100': !isVideoPlaying, 'opacity-0': isVideoPlaying }"
      >
        <div class="bg-black bg-opacity-50 rounded-full p-3">
          <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
          </svg>
        </div>
      </div>
      
      <!-- Loading Spinner (shows while video is loading) -->
      <div 
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30"
      >
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    </div>
    
    <!-- Content Section -->
    <div class="p-4">
      <!-- Type Badge -->
      <div class="mb-3">
        <span class="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/>
          </svg>
          {{ getTypeLabel(video.type) }}
        </span>
      </div>
      
      <!-- Video Name -->
<h3 class="text-lg font-black text-gray-900 mb-2 truncate leading-tight">
  {{
    currentLanguage === 'fr' ? video.name :
    currentLanguage === 'ar' ? video.name_ar :
    currentLanguage === 'en' ? video.name_en :
    currentLanguage === 'ko' ? video.name_ko :
    currentLanguage === 'ja' ? video.name_ja :
    currentLanguage === 'zh' ? video.name_zh :
    currentLanguage === 'ru' ? video.name_ru :
    video.name
  }}
</h3>
      <!-- Duration and Type Info -->
      <p class="text-xs font-semibold text-gray-600 mb-4">
        {{ $t('durration') }}: {{ formatDuration(video.duration) }} 
      </p>
      
      <!-- Watch Video Button -->
      <a 
        :href="`/videos/${this.$props.video.slug}`"
        class="w-full bg-gradient-to-r from-[#F78D1E] to-[#F1772A] hover:from-[#F1772A] hover:to-[#F78D1E] transition-all duration-300 transform hover:scale-105 text-white font-bold py-2.5 px-4 rounded-lg  flex items-center justify-center text-sm shadow-md hover:shadow-lg"
      >
        <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
        </svg>
        {{ $t('watchvideo') }}
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoCard',
  props: {
    video: {
      type: Object,
      required: true,
      default: () => ({
        name: 'Sample Video',
        duration: 117,
        type: 'hotel',
        vignette: '/placeholder.svg?height=240&width=385',
        videoLink: ''
      })
    }
  },
  data() {
    return {
      isVideoPlaying: false,
      isLoading: false,
      hoverTimeout: null,
      currentLanguage: this.$i18n.locale,                    
      videoLoaded: false
    };
  },
  watch: {
    "$i18n.locale"(newLocale) {
        this.currentLanguage = newLocale;
      },
  },
  methods: {
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    
    getTypeLabel(type) {
      const typeMap = {
        'hotel': 'Hotel',
        'hotel Destination': 'Destination',
        'restaurant': 'Restaurant',
        'restaurant Destination': 'Restaurant',
        'artisanat': 'Artisanat'
      };
      return typeMap[type] || 'Video';
    },
    
    handleMouseEnter() {
      // Clear any existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      
      // Start video preview after a short delay (like YouTube)
      this.hoverTimeout = setTimeout(() => {
        this.startVideoPreview();
      }, 500); // 500ms delay before starting video
    },
    
    handleMouseLeave() {
      // Clear timeout if user leaves before delay
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      
      // Stop video preview
      this.stopVideoPreview();
    },
    
    startVideoPreview() {
      if (!this.video.videoLink) return;
      
      const videoElement = this.$refs.videoElement;
      if (videoElement && this.videoLoaded) {
        this.isVideoPlaying = true;
        videoElement.currentTime = 0; // Start from beginning
        videoElement.play().catch(error => {
          console.log('Video autoplay failed:', error);
          this.isVideoPlaying = false;
        });
      } else if (videoElement && !this.videoLoaded) {
        this.isLoading = true;
        // Try to load the video
        videoElement.load();
      }
    },
    
    stopVideoPreview() {
      const videoElement = this.$refs.videoElement;
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
      }
      this.isVideoPlaying = false;
      this.isLoading = false;
    },
    
    onVideoLoaded() {
      this.videoLoaded = true;
      this.isLoading = false;
      
      // If user is still hovering, start playing
      if (this.hoverTimeout) {
        this.startVideoPreview();
      }
    },
    
    watchVideo() {
      window.location.href=`/videos/${this.$props.video.slug}`
    }
  },
  
  beforeUnmount() {
    // Clean up timeout when component is destroyed
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Ensure smooth transitions */
video, img {
  transition: opacity 0.3s ease-in-out;
}
</style>